import axios from "axios";
import { getStoreRef } from "../store";
import { authActions } from "../store/AuthSlice";
import { ONBOARDING_API_URL } from "../constants";
import { SSOAuthorization } from "more-sso-sdk";

let isRefreshing = false;
let failedQueue = [];

const SESSION_EXPIRED_MESSAGE = "Session Expired. Please re-login.";

const processQueue = (error, token = null) => {
  failedQueue.forEach((prom) => {
    if (error) prom.reject(error);
    else prom.resolve(token);
  });
  failedQueue = [];
};

const parseMaybeJson = (value) => {
  if (typeof value !== "string") return value;
  try {
    return JSON.parse(value);
  } catch (error) {
    return value;
  }
};

const isOnboardingRequest = (config) => {
  const url = String(config?.url || "").toLowerCase();
  const onboardingUrl = String(ONBOARDING_API_URL || "").toLowerCase();

  if (onboardingUrl && url.includes(onboardingUrl)) return true;

  // Fallback so refresh still runs if env host formatting differs slightly.
  return url.includes("onboarding_mgmt");
};

const getEnvelopeBody = (data) => {
  if (!data || typeof data !== "object") return null;
  return parseMaybeJson(data.body);
};

const getUnauthorizedMessage = (data) => {
  if (!data || typeof data !== "object") return "";

  const body = getEnvelopeBody(data);
  return String(
    (body && typeof body === "object" ? body.message : "") ||
      data.message ||
      "",
  )
    .trim()
    .toLowerCase();
};

/**
 * Onboarding APIs often return API Gateway envelopes with HTTP 200 and
 * `{ statusCode: 401, body: { status: "error", message: "Invalid token." } }`.
 * Treat those the same as a real HTTP 401 so the shared refresh flow runs.
 */
const isUnauthorizedEnvelope = (data) => {
  if (!data || typeof data !== "object") return false;

  const message = getUnauthorizedMessage(data);
  const isInvalidToken =
    message === "invalid token." ||
    message === "invalid token" ||
    message.includes("invalid token");

  if (!isInvalidToken) return false;

  const statusCode = data.statusCode ?? data.status_code;
  if (statusCode === 401 || statusCode === "401") return true;

  const body = getEnvelopeBody(data);
  const status = String(
    (body && typeof body === "object" ? body.status : "") || data.status || "",
  ).toLowerCase();

  return status === "error" || status === "failed";
};

const buildUnauthorizedError = (response) => {
  const error = new Error("Unauthorized");
  error.config = response.config;
  error.response = {
    status: 401,
    data: response.data,
    headers: response.headers,
    config: response.config,
    statusText: "Unauthorized",
  };
  error.isAxiosError = true;
  return error;
};

const buildSessionExpiredError = () => new Error(SESSION_EXPIRED_MESSAGE);

const rejectSessionExpired = (dispatch) => {
  dispatch(authActions.setLoggedIn(false));
  return Promise.reject(buildSessionExpiredError());
};

const instance = axios.create();

/**
 * Shared refresh + retry used by:
 * - Real HTTP 401 responses (other modules)
 * - Onboarding HTTP 200 envelopes with statusCode 401 / "Invalid token."
 *
 * Note: rejecting from a response fulfillment handler does NOT enter the
 * rejection handler of the same interceptor, so onboarding must call this
 * directly instead of only Promise.reject(...).
 */
const handleUnauthorizedRequest = async (error) => {
  const originalRequest = error?.config;

  if (!originalRequest) {
    return Promise.reject(error);
  }

  // Already retried once after refresh — treat as expired session.
  if (originalRequest._retry) {
    const store = getStoreRef();
    return rejectSessionExpired(store.dispatch);
  }

  originalRequest._retry = true;

  if (isRefreshing) {
    return new Promise((resolve, reject) => {
      failedQueue.push({ resolve, reject });
    })
      .then((newToken) => {
        originalRequest.headers = originalRequest.headers || {};
        originalRequest.headers.authorization = newToken;
        return instance(originalRequest);
      })
      .catch((err) => Promise.reject(err));
  }

  isRefreshing = true;

  const store = getStoreRef();
  const dispatch = store.dispatch;

  try {
    let refreshResponse = null;

    if (process.env.NODE_ENV === "development") {
      refreshResponse =
        await SSOAuthorization.getDevToken("abhishek.pattjoshi");
    } else {
      refreshResponse = await SSOAuthorization.getToken();
    }

    const newToken = refreshResponse?.token;
    console.log("\n\nAxios Interceptor Token : ", refreshResponse);

    if (!newToken) throw new Error("SSO returned no token");

    dispatch(authActions.setLoginDetails(refreshResponse));
    dispatch(authActions.setLoggedIn(true));

    processQueue(null, newToken);
    originalRequest.headers = originalRequest.headers || {};
    originalRequest.headers.authorization = newToken;
    return instance(originalRequest);
  } catch (refreshError) {
    processQueue(buildSessionExpiredError(), null);
    return rejectSessionExpired(dispatch);
  } finally {
    isRefreshing = false;
  }
};

instance.interceptors.request.use((config) => {
  const state = getStoreRef().getState();
  const token = state.auth.accessToken;

  if (token) {
    config.headers = config.headers || {};
    config.headers.authorization = token;
  }

  return config;
});

instance.interceptors.response.use(
  (response) => {
    // Onboarding: HTTP 200 + Invalid token envelope → same refresh path as HTTP 401
    if (
      isOnboardingRequest(response?.config) &&
      isUnauthorizedEnvelope(response?.data)
    ) {
      return handleUnauthorizedRequest(buildUnauthorizedError(response));
    }
    return response;
  },

  async (error) => {
    if (error?.response?.status !== 401) {
      return Promise.reject(error);
    }

    return handleUnauthorizedRequest(error);
  },
);

const axiosClient = instance;
export default axiosClient;
